import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector, useDispatch } from 'react-redux';
import { fetchBatchData } from 'thunks/trainingThunks/thunk';
import { fetchAttendanceByBatchId } from 'thunks/attendenceSliceThunks/thunks';
import Layout from 'Component/Layouts/layout';
import { toast } from 'react-toastify';
import rootAxios from 'requests/rootAxios';
import TableComponent from 'Component/Table';
import { fetchAvailableCenters } from 'thunks/centersThunks';
import { fetchBranchFeedback } from 'thunks/branchFeedbackThunks/thunks';

const BranchInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const batchId = queryParams.get('batchId');
  const dispatch = useDispatch();

  const { data: attendenceData, isLoading: attendanceLoading } = useSelector(
    (state) => state.attendenceSlice
  );
  const { trainingType, branch, batchTrainees, region } = useSelector(
    (state) => state.trainingSlice
  );
  const { token } = useSelector((state) => state.authSlice);
  const {
    data: branchFeedbackData,
    loading: branchFeedbackLoading,
    error: branchFeedbackError,
  } = useSelector((state) => state.branchFeedbackSlice);

  const [submitting, setSubmitting] = useState(false);
  const {
    availableCenters,
    loading,
    error: centersError,
  } = useSelector((state) => state.centersSlice);

  // Form state
  const [formData, setFormData] = useState({
    visitDate: '',
    visitType: '', // 'Branch Visit' or 'VC'
    batchCode: '',
    branchVisitTime: {
      inTime: '',
      outTime: '17:00',
    },
    branchCode: '',
    branchName: '',
    regionName: '',
    circleHeadName: '',
    bmName: '',
    tmDrmName: '',
    rmName: '',
    employees: [],
    branchDetails: {
      workingSystems: {
        tab: '',
        laptop: '',
        biometricDevices: '',
        printer: '',
      },
      nonWorkingSystems: {
        tab: '',
        laptop: '',
        biometricDevices: '',
        printer: '',
      },
      cleanliness: '',
      branding: {
        status: '',
        maintenance: '',
      },
      groomingObservation: '',
      highlights: ['', '', ''],
      opportunities: ['', '', ''],
    },
    centers: [],
    trainingTopics: [],
  });

  // Fetch batch data and attendance data
  useEffect(() => {
    if (batchId) {
      dispatch(fetchBatchData({ dispatch, batchId }));
      dispatch(fetchAttendanceByBatchId(batchId));
      dispatch(fetchBranchFeedback(batchId));
    }
  }, [batchId, dispatch]);

  // Pre-fill form with fetched branch feedback data
  useEffect(() => {
    if (branchFeedbackData) {
      const isObject = (obj) => obj && typeof obj === 'object' && !Array.isArray(obj);

      const mergeDeep = (target, source) => {
        const output = { ...target };
        if (isObject(target) && isObject(source)) {
          Object.keys(source).forEach((key) => {
            // Do not merge employees, let it be handled by attendance data
            if (key === 'employees') {
              return;
            }
            if (isObject(source[key])) {
              if (key in target && isObject(target[key])) {
                output[key] = mergeDeep(target[key], source[key]);
              } else {
                output[key] = source[key];
              }
            } else {
              output[key] = source[key];
            }
          });
        }
        return output;
      };

      const feedbackDataWithFormattedDate = {
        ...branchFeedbackData,
        visitDate: branchFeedbackData.visitDate
          ? branchFeedbackData.visitDate.split('T')[0]
          : '',
      };

      setFormData((prev) => mergeDeep(prev, feedbackDataWithFormattedDate));
    }
  }, [branchFeedbackData]);

  // Fetch centers from API
  useEffect(() => {
    if (branch && branch.length > 0) {
      dispatch(fetchAvailableCenters({ branch, batchId }));
    }
  }, [branch, batchId, dispatch]);

  // Auto-populate fields when batch data is available
  useEffect(() => {
    if (batchId && branch && branch.length > 0) {
      const firstBranch = branch[0];
      setFormData((prev) => ({
        ...prev,
        batchCode: batchId,
        branchCode: firstBranch.id || '',
        branchName: firstBranch.label || '',
        regionName: region?.[0]?.label, // Will be populated from branch data
      }));
    }
  }, [batchId, branch]);

  // Auto-populate employees from attendance API data
  useEffect(() => {
    if (
      attendenceData?.data?.batchTrainees &&
      attendenceData.data.batchTrainees.length > 0 &&
      attendenceData?.data?.attendance &&
      attendenceData.data.attendance.length > 0
    ) {
      const lastAttendance =
        attendenceData.data.attendance[attendenceData.data.attendance.length - 1];
      const employees = attendenceData.data.batchTrainees.map((trainee, index) => {
        const isPresent = lastAttendance?.present?.includes(trainee.id);
        const isAbsent = lastAttendance?.absent?.includes(trainee.id);
        let attendanceStatus = 'NA';
        if (isPresent) {
          attendanceStatus = 'Present';
        } else if (isAbsent) {
          const reason = lastAttendance?.absentReasons?.[String(trainee.id)] || '';
          if (reason.includes('Absconding')) {
            attendanceStatus = 'Absent (Absconding)';
          } else if (reason.includes('DNJ') || reason.includes('Did Not Join')) {
            attendanceStatus = 'Absent (DNJ)';
          } else if (reason.includes('Resigned')) {
            attendanceStatus = 'Absent (Resigned)';
          } else if (reason.includes('Moved to other branch')) {
            attendanceStatus = 'Absent (Moved to other branch)';
          } else {
            attendanceStatus = 'Absent';
          }
        }

        return {
          sno: index + 1,
          employeeCode: trainee.id,
          employeeName: trainee.name || `Employee ${trainee.id}`,
          role: trainee.role || '',
          branchName: formData.branchName || '',
          regionName: formData.regionName || '',
          attendance: attendanceStatus,
        };
      });

      setFormData((prev) => ({
        ...prev,
        employees,
      }));
    }
  }, [attendenceData, formData.branchName, formData.regionName]);

  // Auto-populate in-time from attendance record
  useEffect(() => {
    if (attendenceData?.data?.attendance && attendenceData.data.attendance.length > 0) {
      const lastAttendance =
        attendenceData.data.attendance[attendenceData.data.attendance.length - 1];
      if (lastAttendance.date) {
        // Extract time from date if available, or use a default
        const dateObj = new Date(lastAttendance.date);
        const hours = dateObj.getHours().toString().padStart(2, '0');
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        const inTime = `${hours}:${minutes}`;

        setFormData((prev) => {
          const newFormData = { ...prev };
          if (!newFormData.visitDate) {
            newFormData.visitDate = lastAttendance.date.split('T')[0] || '';
          }
          if (!newFormData.branchVisitTime.inTime) {
            newFormData.branchVisitTime = {
              ...newFormData.branchVisitTime,
              inTime: inTime || '09:00',
            };
          }
          return newFormData;
        });
      }
    }
  }, [attendenceData]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value,
      },
    }));
  };

  const handleDeepNestedInputChange = (parentField, childField, grandChildField, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: {
          ...prev[parentField][childField],
          [grandChildField]: value,
        },
      },
    }));
  };

  const handleArrayInputChange = (parentField, childField, index, value) => {
    setFormData((prev) => {
      const newParent = { ...prev[parentField] };
      const newArray = [...(newParent[childField] || [])];
      newArray[index] = value;
      return {
        ...prev,
        [parentField]: {
          ...newParent,
          [childField]: newArray,
        },
      };
    });
  };

  const handleCenterChange = (centerIndex, field, value) => {
    setFormData((prev) => {
      const newCenters = [...prev.centers];
      newCenters[centerIndex] = {
        ...newCenters[centerIndex],
        [field]: value,
      };
      return {
        ...prev,
        centers: newCenters,
      };
    });
  };

  const handleCenterArrayChange = (centerIndex, field, arrayIndex, value) => {
    setFormData((prev) => {
      const newCenters = [...prev.centers];
      const newArray = [...(newCenters[centerIndex][field] || [])];
      newArray[arrayIndex] = value;
      newCenters[centerIndex] = {
        ...newCenters[centerIndex],
        [field]: newArray,
      };
      return {
        ...prev,
        centers: newCenters,
      };
    });
  };

  const handleAddCenter = () => {
    setFormData((prev) => ({
      ...prev,
      centers: [
        ...prev.centers,
        {
          centerId: '',
          highlights: ['', '', ''],
          opportunities: ['', '', ''],
          scvPunched: '',
        },
      ],
    }));
  };

  const handleRemoveCenter = (centerIndex) => {
    setFormData((prev) => ({
      ...prev,
      centers: prev.centers.filter((_, index) => index !== centerIndex),
    }));
  };

  const handleAddTrainingTopic = () => {
    setFormData((prev) => ({
      ...prev,
      trainingTopics: [
        ...prev.trainingTopics,
        {
          topic: '',
          covered: '',
        },
      ],
    }));
  };

  const handleTrainingTopicChange = (index, field, value) => {
    setFormData((prev) => {
      const newTopics = [...prev.trainingTopics];
      newTopics[index] = {
        ...newTopics[index],
        [field]: value,
      };
      return {
        ...prev,
        trainingTopics: newTopics,
      };
    });
  };

  const handleRemoveTrainingTopic = (index) => {
    setFormData((prev) => ({
      ...prev,
      trainingTopics: prev.trainingTopics.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const result = await submitBranchFeedback(formData);
    setSubmitting(false);
    if (result) {
      navigate(`/attendance/feedback?batchId=${batchId}`);
    }
  };

  const submitBranchFeedback = async (formData) => {
    try {
      const response = await rootAxios.post('/user/branchFeedback', {
        batchId,
        ...formData,
      });
      toast.success('Branch feedback submitted successfully!', { theme: 'colored' });
      return response.data;
    } catch (error) {
      console.error('Error submitting branch feedback:', error);
      toast.error(`Failed to submit branch feedback: ${error.message}`, { theme: 'colored' });
      return null;
    }
  };

  const employeeColumns = [
    { label: 'S. NO.', field: 'sno' },
    { label: 'EMPLOYEE CODE', field: 'employeeCode' },
    { label: 'EMPLOYEE NAME', field: 'employeeName' },
    { label: 'BRANCH NAME', field: 'branchName' },
    { label: 'REGION NAME', field: 'regionName' },
    {
      label: 'ATTENDANCE',
      field: 'attendance',
    },
  ];

  return (
    <Layout>
      <Box sx={{ padding: '20px' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px',
            gap: 2,
          }}
        >
          <IconButton
            onClick={() => navigate(`/attendance/mark?batchId=${batchId}`)}
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            Branch Information - Batch {batchId}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper sx={{ padding: '20px' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date of Branch Visit / VC"
                    type="date"
                    value={formData.visitDate}
                    onChange={(e) => handleInputChange('visitDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Visit Type</InputLabel>
                    <Select
                      value={formData.visitType}
                      onChange={(e) => handleInputChange('visitType', e.target.value)}
                      label="Visit Type"
                      required
                    >
                      <MenuItem value="Branch Visit">Branch Visit</MenuItem>
                      <MenuItem value="VC">VC</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Batch Code"
                    value={formData.batchCode}
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Branch Visit Time - In Time"
                    type="time"
                    value={formData.branchVisitTime.inTime}
                    onChange={(e) =>
                      handleNestedInputChange('branchVisitTime', 'inTime', e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Branch Visit Time - Out Time"
                    type="time"
                    value={formData.branchVisitTime.outTime}
                    onChange={(e) =>
                      handleNestedInputChange('branchVisitTime', 'outTime', e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Branch Code"
                    value={formData.branchCode}
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Branch Name"
                    value={formData.branchName}
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Region Name"
                    value={formData.regionName}
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Circle Head Name"
                    value={formData.circleHeadName}
                    onChange={(e) => handleInputChange('circleHeadName', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="BM Name"
                    value={formData.bmName}
                    onChange={(e) => handleInputChange('bmName', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="TM / DRM Name"
                    value={formData.tmDrmName}
                    onChange={(e) => handleInputChange('tmDrmName', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="RM Name"
                    value={formData.rmName}
                    onChange={(e) => handleInputChange('rmName', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Employees Table */}
          <Grid item xs={12}>
            <Paper sx={{ padding: '20px' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                Employees
              </Typography>
              {attendanceLoading ? (
                <Box
                  sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
                >
                  <CircularProgress />
                </Box>
              ) : formData.employees.length > 0 ? (
                <TableComponent
                  tableData={formData.employees}
                  columns={employeeColumns}
                  tableName="Employees"
                  count={formData.employees.length}
                  useWholeRowClicked={false}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No employees data available
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Branch Details */}
          <Grid item xs={12}>
            <Paper sx={{ padding: '20px' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                Branch Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Count of Working Systems
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="TAB"
                        type="number"
                        value={formData.branchDetails.workingSystems.tab}
                        onChange={(e) =>
                          handleDeepNestedInputChange(
                            'branchDetails',
                            'workingSystems',
                            'tab',
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="LAPTOP"
                        type="number"
                        value={formData.branchDetails.workingSystems.laptop}
                        onChange={(e) =>
                          handleDeepNestedInputChange(
                            'branchDetails',
                            'workingSystems',
                            'laptop',
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="BIOMETRIC DEVICES"
                        type="number"
                        value={formData.branchDetails.workingSystems.biometricDevices}
                        onChange={(e) =>
                          handleDeepNestedInputChange(
                            'branchDetails',
                            'workingSystems',
                            'biometricDevices',
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="PRINTER"
                        type="number"
                        value={formData.branchDetails.workingSystems.printer}
                        onChange={(e) =>
                          handleDeepNestedInputChange(
                            'branchDetails',
                            'workingSystems',
                            'printer',
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Count of Non Working Systems
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="TAB"
                        type="number"
                        value={formData.branchDetails.nonWorkingSystems.tab}
                        onChange={(e) =>
                          handleDeepNestedInputChange(
                            'branchDetails',
                            'nonWorkingSystems',
                            'tab',
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="LAPTOP"
                        type="number"
                        value={formData.branchDetails.nonWorkingSystems.laptop}
                        onChange={(e) =>
                          handleDeepNestedInputChange(
                            'branchDetails',
                            'nonWorkingSystems',
                            'laptop',
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="BIOMETRIC DEVICES"
                        type="number"
                        value={formData.branchDetails.nonWorkingSystems.biometricDevices}
                        onChange={(e) =>
                          handleDeepNestedInputChange(
                            'branchDetails',
                            'nonWorkingSystems',
                            'biometricDevices',
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="PRINTER"
                        type="number"
                        value={formData.branchDetails.nonWorkingSystems.printer}
                        onChange={(e) =>
                          handleDeepNestedInputChange(
                            'branchDetails',
                            'nonWorkingSystems',
                            'printer',
                            e.target.value
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Cleanliness of Store Walls / Floor / Doors</InputLabel>
                    <Select
                      value={formData.branchDetails.cleanliness}
                      onChange={(e) =>
                        handleNestedInputChange('branchDetails', 'cleanliness', e.target.value)
                      }
                      label="Cleanliness of Store Walls / Floor / Doors"
                    >
                      <MenuItem value="VERY CLEAN">VERY CLEAN</MenuItem>
                      <MenuItem value="CLEAN">CLEAN</MenuItem>
                      <MenuItem value="UNCLEAN">UNCLEAN</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Branding / Glow Sign Boards Status</InputLabel>
                    <Select
                      value={formData.branchDetails.branding.status}
                      onChange={(e) =>
                        handleNestedInputChange('branchDetails', 'branding', {
                          ...formData.branchDetails.branding,
                          status: e.target.value,
                          maintenance:
                            e.target.value === 'DISPLAYED'
                              ? formData.branchDetails.branding.maintenance
                              : '',
                        })
                      }
                      label="Branding / Glow Sign Boards Status"
                    >
                      <MenuItem value="DISPLAYED">DISPLAYED</MenuItem>
                      <MenuItem value="NOT DISPLAYED">NOT DISPLAYED</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {formData.branchDetails.branding.status === 'DISPLAYED' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Maintenance Status</InputLabel>
                      <Select
                        value={formData.branchDetails.branding.maintenance}
                        onChange={(e) =>
                          handleNestedInputChange('branchDetails', 'branding', {
                            ...formData.branchDetails.branding,
                            maintenance: e.target.value,
                          })
                        }
                        label="Maintenance Status"
                      >
                        <MenuItem value="WELL MAINTAINED">WELL MAINTAINED</MenuItem>
                        <MenuItem value="DAMAGED OR DULL">DAMAGED OR DULL</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Grooming Observation</InputLabel>
                    <Select
                      value={formData.branchDetails.groomingObservation}
                      onChange={(e) =>
                        handleNestedInputChange(
                          'branchDetails',
                          'groomingObservation',
                          e.target.value
                        )
                      }
                      label="Grooming Observation"
                    >
                      <MenuItem value="VERY GOOD">VERY GOOD</MenuItem>
                      <MenuItem value="GOOD">GOOD</MenuItem>
                      <MenuItem value="POOR">POOR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Highlights of the Branch
                  </Typography>
                  {formData.branchDetails.highlights.map((highlight, index) => (
                    <TextField
                      key={index}
                      fullWidth
                      label={`Highlight ${index + 1}`}
                      value={highlight}
                      onChange={(e) =>
                        handleArrayInputChange('branchDetails', 'highlights', index, e.target.value)
                      }
                      multiline
                      rows={2}
                      sx={{ mb: 2 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  ))}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Opportunities / Recommendation for the Branch
                  </Typography>
                  {formData.branchDetails.opportunities.map((opportunity, index) => (
                    <TextField
                      key={index}
                      fullWidth
                      label={`Opportunity / Recommendation ${index + 1}`}
                      value={opportunity}
                      onChange={(e) =>
                        handleArrayInputChange(
                          'branchDetails',
                          'opportunities',
                          index,
                          e.target.value
                        )
                      }
                      multiline
                      rows={2}
                      sx={{ mb: 2 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  ))}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Centers Section Header */}
          <Grid item xs={12}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Centers
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCenter}
                sx={{ minWidth: 150 }}
              >
                Add Center
              </Button>
            </Box>
          </Grid>

          {/* Centers List */}
          {formData.centers.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ padding: '20px', textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  No centers added yet. Click "Add Center" to add a center.
                </Typography>
              </Paper>
            </Grid>
          ) : (
            formData.centers.map((center, centerIndex) => (
              <Grid item xs={12} key={centerIndex}>
                <Paper sx={{ padding: '20px', position: 'relative' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ color: 'primary.main' }}>
                      CENTER {centerIndex + 1}
                    </Typography>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveCenter(centerIndex)}
                      sx={{ position: 'absolute', top: 16, right: 16 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={availableCenters.map((c) => ({
                          id: c,
                          label: c,
                        }))}
                        value={
                          center.centerId
                            ? {
                              id: center.centerId,
                              label: center.centerId || '',
                            }
                            : null
                        }
                        onChange={(event, newValue) => {
                          handleCenterChange(centerIndex, 'centerId', newValue?.id || '');
                        }}
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? option : option.label || ''
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Centre Visited"
                            placeholder="Select Center"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>SCV Punched</InputLabel>
                        <Select
                          value={center.scvPunched}
                          onChange={(e) =>
                            handleCenterChange(centerIndex, 'scvPunched', e.target.value)
                          }
                          label="SCV Punched"
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Highlights of the Centre {centerIndex + 1}
                      </Typography>
                      {center.highlights.map((highlight, index) => (
                        <TextField
                          key={index}
                          fullWidth
                          label={`Highlight ${index + 1}`}
                          value={highlight}
                          onChange={(e) =>
                            handleCenterArrayChange(
                              centerIndex,
                              'highlights',
                              index,
                              e.target.value
                            )
                          }
                          multiline
                          rows={2}
                          sx={{ mb: 2 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      ))}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Opportunities / Recommendation for the Centre {centerIndex + 1}
                      </Typography>
                      {center.opportunities.map((opportunity, index) => (
                        <TextField
                          key={index}
                          fullWidth
                          label={`Opportunity / Recommendation ${index + 1}`}
                          value={opportunity}
                          onChange={(e) =>
                            handleCenterArrayChange(
                              centerIndex,
                              'opportunities',
                              index,
                              e.target.value
                            )
                          }
                          multiline
                          rows={2}
                          sx={{ mb: 2 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      ))}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))
          )}

          {/* Training Details */}
          <Grid item xs={12}>
            <Paper sx={{ padding: '20px' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                TRAINING DETAILS
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Topics Covered during the Corrective / Refresher Training
              </Typography>
              {formData.trainingTopics.map((topic, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={8}>
                    <Autocomplete
                      freeSolo
                      options={[]} // TODO: Pre-fill from audit report observations
                      value={topic.topic}
                      onChange={(event, newValue) => {
                        handleTrainingTopicChange(
                          index,
                          'topic',
                          typeof newValue === 'string' ? newValue : newValue?.label || ''
                        );
                      }}
                      onInputChange={(event, newInputValue) => {
                        handleTrainingTopicChange(index, 'topic', newInputValue);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Topic" placeholder="Enter or select topic" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Covered</InputLabel>
                      <Select
                        value={topic.covered}
                        onChange={(e) =>
                          handleTrainingTopicChange(index, 'covered', e.target.value)
                        }
                        label="Covered"
                      >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleRemoveTrainingTopic(index)}
                      sx={{ height: '100%' }}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              ))}
              <Button variant="outlined" onClick={handleAddTrainingTopic} sx={{ mt: 2 }}>
                Add Topic
              </Button>
            </Paper>
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/attendance/mark?batchId=${batchId}`)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !formData.visitDate || !formData.visitType}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
              >
                {submitting ? 'Submitting...' : 'Submit & Continue to Feedback'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default BranchInfo;
